import * as moment from 'moment';
import { MOMENT_FORMATS } from '@/utils/cons.js';
import { getCard, getCards, createCard, editCard, deleteCard, setAutoRenewal } from '@/api';

const getInitialState = () => ({
  cards: [],
});

export default {
  namespaced: true,
  state: getInitialState(),
  getters: {
    parseCard: (state) => (resp) => {
      resp.expiry_date = moment.utc(resp.expiry_date * 1000).format(MOMENT_FORMATS.CARD_EXPIRY_DATE);
      const [month, year] = resp.expiry_date.split('/');
      resp.expiry_sort = `${year}${month}`;
      resp.number = '*** ' + resp.last_digits;
      resp.isActive = true;
      // Checked statuses in table
      resp.isDisabledState = false;
      resp.isCheckedState = false;
      resp.isActiveState = false;
      return resp;
    },
    getAttributes: (state) => (attr) => {
      return {
        ...(attr.id && { id: attr.id }),
        ...(attr.expiry_date && { expiry_date: attr.expiry_date }),
        ...(attr.token && { token: attr.token }),
        ...(attr.number && { number: attr.number.replace(/\s/g, '') }),
        ...{ name: attr.name || '' },
        ...(attr.holder_name && { holder_name: attr.holder_name }),
        ...(attr.security_code && { security_code: attr.security_code }),
        ...(attr.billing_address && {
          billing_address: {
            ...(attr.billing_address.address1 && { address1: attr.billing_address.address1 }),
            ...(attr.billing_address.address2 && { address2: attr.billing_address.address2 }),
            ...(attr.billing_address.address3 && { address3: attr.billing_address.address3 }),
            ...(attr.billing_address.city && { city: attr.billing_address.city }),
            ...(attr.billing_address.zip && { zip: attr.billing_address.zip }),
            ...(attr.billing_address.country_code && { country_code: attr.billing_address.country_code }),
            ...(attr.billing_address.country_state && { country_state: attr.billing_address.country_state }),
          },
        }),
        ...(attr.type && { type: attr.type }),
        ...(typeof attr.auto_renew !== 'undefined' && { auto_renew: attr.auto_renew }),
        ...(typeof attr.isActive !== 'undefined' && { status: attr.isActive ? 'A' : 'D' }),
        ...(attr.invoice_id && { invoice_id: attr.invoice_id }),
        ...(attr.strong_customer_authentication && {
          strong_customer_authentication: {
            browser_javascript_enabled: true,
            browser_java_enabled: navigator.javaEnabled(),
            browser_language: navigator.language,
            browser_color_depth: screen.colorDepth.toString(),
            browser_screen_height: screen.height.toString(),
            browser_screen_width: screen.width.toString(),
            browser_tz: new Date().getTimezoneOffset().toString(),
          },
        }),
      };
    },
    getCardById: (state) => (id) => {
      return state.cards.find((card) => card.id === id);
    },
  },
  mutations: {
    setCards(state, cards) {
      state.cards = cards;
    },
    addCard(state, card) {
      state.cards.push(card);
    },
    updateCard(state, card) {
      const cardIndex = state.cards.findIndex((item) => item.id === card.id);
      const { isActiveState, isCheckedState, isDisabledState } = state.cards[cardIndex];
      const updatedCard = { ...card, isCheckedState, isActiveState, isDisabledState };
      state.cards[cardIndex] = updatedCard;
    },
    deleteCard(state, card_id) {
      state.cards.forEach((s_card, index) => {
        if (s_card.id === card_id) {
          state.cards.splice(index, 1);
        }
      });
    },
    resetState(state) {
      Object.assign(state, getInitialState());
    },
    setAutopaymentCard(state, auto_payment_card) {
      const cardId = auto_payment_card || state.cards.find((card) => card.auto_payment_card)?.auto_payment_card;
      state.cards = state.cards.map((card) => ({ ...card, isAutoPaymentCard: card.id === cardId }));
    },
  },
  actions: {
    async getCards({ getters, commit }) {
      const { data } = await getCards();
      if (data?.length > 0) {
        let cards = data.map((card) => getters.parseCard(card));
        commit('setCards', cards);
        commit('setAutopaymentCard');
      }
      return data;
    },
    async getCard({ getters, commit }, id) {
      const { data } = await getCard(id);
      const newCard = getters.parseCard(data);
      commit('updateCard', newCard);
      return data;
    },
    async addCard({ getters, commit }, options) {
      const payload = getters.getAttributes(options);
      const response = await createCard(payload);
      if (response.status === 307) {
        return response;
      }
      if (response.status === 200) {
        const newCard = getters.parseCard(response.data);
        commit('addCard', newCard);
        return response.data;
      }
      throw response;
    },
    async editCard({ getters, commit }, options = {}) {
      const { data } = await editCard(options.id, getters.getAttributes(options));
      const newCard = getters.parseCard(data);
      commit('updateCard', newCard);
      return data;
    },
    async deleteCard({ commit }, id) {
      await deleteCard(id);
      commit('deleteCard', id);
      return;
    },
    async setAutoRenewal({ commit }, data) {
      const resp = await setAutoRenewal(data);
      if (resp) {
        commit('setAutopaymentCard', resp.data.auto_payment_card);
      }
      return resp;
    },
  },
};
